import { useChatDataQuery } from '@/app/chat/_common/use-chat-data-query'
import { Button } from '@/components/ui/button'
import { TabsTrigger } from '@/components/ui/tabs'
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button'
import { cn } from '@/lib/utils'
import { useSortable } from '@dnd-kit/sortable'
import { cva, type VariantProps } from 'class-variance-authority'
import { XIcon } from 'lucide-react'
import React from 'react'
import { ChatOptionsMenu } from '../chat-options-menu'
import { useSplitView } from './split-view-context'
import { getTabMetadata, TabIcon } from './tabs/metadata'
import { isWorkflowTab, Tab, TAB_TYPE_CHAT, TabType } from './tabs/types'

type DraggableTabProps = Readonly<
  DraggableTabVariantProps & {
    tab: Tab
  }
>

const DraggableTab = React.forwardRef<HTMLButtonElement, DraggableTabProps>(
  ({ tab, variant }, ref) => {
    const { tabsLeft, tabsRight, filterTabs, tabKeyDownHandler, getPanelState } = useSplitView()
    const { panelId } = getPanelState({ tabId: tab.id })
    const { attributes, listeners, setNodeRef } = useSortable({
      id: tab.id,
      animateLayoutChanges: () => true,
    })

    const onCloseHandler = React.useMemo(() => {
      if (tabsLeft.length + tabsRight.length === 1) return
      return () => filterTabs({ tabId: tab.id })
    }, [tabsLeft.length, tabsRight.length, tab.id, filterTabs])

    const buttonProps = {
      variant,
      title: tab.title,
      ref: (node: HTMLButtonElement | null) => {
        setNodeRef(node)
        if (typeof ref === 'function') ref(node)
        else if (ref) ref.current = node
      },
      onKeyDown: (e: React.KeyboardEvent) => tabKeyDownHandler(e, panelId),
      ...attributes,
      ...listeners,
    }

    return (
      <TabsTrigger unstyled asChild value={tab.id}>
        {isWorkflowTab(tab) ? (
          <ChatTabButton {...buttonProps} workflowId={tab.workflowId} />
        ) : (
          <TabButton {...buttonProps} tabType={tab.type} onClose={onCloseHandler} />
        )}
      </TabsTrigger>
    )
  },
)
DraggableTab.displayName = 'DraggableTab'

type TabButtonProps = React.PropsWithChildren<
  {
    variant: DraggableTabVariantProps['variant']
    tabType: TabType
    title: string
    onClose?: () => void
  } & React.ComponentPropsWithoutRef<'button'>
>

const TabButton = React.forwardRef<HTMLButtonElement, TabButtonProps>(
  ({ variant, tabType, title, onClose, className, children, ...props }, ref) => (
    <Button
      ref={ref}
      unstyled
      className={cn(draggableTabVariants({ variant }), className)}
      {...props}
    >
      <TabIcon
        type={tabType}
        className={cn(
          'size-5 shrink-0 text-neutral-150',
          variant === 'dragging' && 'text-mango-300',
        )}
      />
      <span className='grow truncate text-left'>{title}</span>
      {onClose && (
        <TooltipIconButton
          size='auto'
          delayDuration={1200}
          asChild
          titleString='Close'
          onPointerDown={(e) => e.preventDefault()}
          onPointerUp={onClose}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onClose()
            }
          }}
        >
          <span className='group/close size-5 text-neutral-150 transition-opacity ease-in-out group-focus-visible/close:text-mango-300 hover:bg-black/5 focus-visible:bg-transparent focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-mango-300 active:bg-black/5'>
            <XIcon strokeWidth={2} className='size-4' />
          </span>
        </TooltipIconButton>
      )}
      {children}
    </Button>
  ),
)
TabButton.displayName = 'TabButton'

type ChatTabButtonProps = Readonly<
  {
    workflowId: string
    variant: DraggableTabVariantProps['variant']
    title?: string | null
  } & React.ComponentPropsWithoutRef<'button'>
>

const ChatTabButton = React.forwardRef<HTMLButtonElement, ChatTabButtonProps>(
  ({ workflowId, variant, title, ...props }, ref) => {
    const { data: chatData } = useChatDataQuery({ workflowId })
    return (
      <TabButton
        ref={ref}
        variant={variant}
        tabType={TAB_TYPE_CHAT}
        title={chatData?.title ?? title ?? getTabMetadata(TAB_TYPE_CHAT).title}
        {...props}
      >
        <ChatOptionsMenu
          asChild
          includeCloseTab
          workflowId={workflowId}
          workflowTitle={chatData?.title}
        />
      </TabButton>
    )
  },
)
ChatTabButton.displayName = 'ChatTabButton'

const draggableTabVariants = cva(
  'w-full h-9 overflow-hidden flex min-w-16 items-center justify-between gap-1.5 whitespace-nowrap rounded-t p-2 text-sm font-light outline outline-0 -outline-offset-[2px] outline-mango-300 focus-visible:outline-[2px]',
  {
    variants: {
      variant: {
        default: 'bg-neutral-50 text-neutral-500',
        active: 'bg-neutral-25 text-neutral-500',
        dragging:
          'bg-neutral-25 text-mango-300 outline-[2px] -outline-offset-[2px] outline-mango-300',
        ghost: 'bg-neutral-75 [&>*]:invisible opacity-35',
        dropTarget:
          '-outline-offset-[2px] outline-mango-300 outline-[2px] outline-dashed bg-neutral-75 [&>*]:invisible',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)
type DraggableTabVariantProps = VariantProps<typeof draggableTabVariants>

export {
  DraggableTab,
  draggableTabVariants,
  TabButton,
  type DraggableTabProps,
  type DraggableTabVariantProps,
}
